import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../context/AuthContext';
import './Auth.css';

function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated } = useAuth();

  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Check for redirect query parameter (e.g. ?redirect=/courses/backend-dev?mode=offline) or router state
  const searchParams = new URLSearchParams(location.search);
  const redirectQuery = searchParams.get('redirect');
  const redirectPath = redirectQuery || location.state?.from || '/courses';

  React.useEffect(() => {
    if (isAuthenticated) {
      navigate(redirectPath, { replace: true });
    }
  }, [isAuthenticated, navigate, redirectPath]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (error) setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!formData.email.trim() || !formData.password) {
      setError('Please provide both email and password.');
      return;
    }

    try {
      setLoading(true);
      await login({
        email: formData.email.trim(),
        password: formData.password
      });

      navigate(redirectPath, { replace: true });
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message || 'Invalid email or password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Student Login | Durbhasi Gurukulam Courses</title>
        <meta name="description" content="Sign in to your Durbhasi Gurukulam student account to access your live cohort hubs and course materials." />
      </Helmet>

      <div className="auth-page-wrapper">
        <div className="auth-card-container">
          {/* Left Brand Panel */}
          <div className="auth-brand-panel">
            <div>
              <div className="auth-brand-header">
                <img
                  src="/durbhasi-gurukulam-logo.png"
                  alt="Durbhasi Gurukulam"
                  className="auth-brand-logo"
                  onError={(e) => { e.currentTarget.style.display = 'none'; }}
                />
                <div>
                  <h1 className="auth-brand-title">Durbhasi <span>Gurukulam</span></h1>
                  <span className="auth-brand-tagline">Systems &amp; Software Engineering</span>
                </div>
              </div>

              <div className="auth-hero-copy">
                <h2>Welcome Back to Your Engineering Journey</h2>
                <p>
                  Access your active live cohorts, mentor code review milestones, and verifiable course credentials.
                </p>

                <ul className="auth-perks-list">
                  <li className="auth-perk-item">
                    <span className="auth-perk-icon">✓</span>
                    <span>Live Interactive Cohort Zoom &amp; Meet Hubs</span>
                  </li>
                  <li className="auth-perk-item">
                    <span className="auth-perk-icon">✓</span>
                    <span>Jaipur Classroom Digital Pass &amp; Lab Seating</span>
                  </li>
                  <li className="auth-perk-item">
                    <span className="auth-perk-icon">✓</span>
                    <span>Direct Mentor Pull Request Defense</span>
                  </li>
                  <li className="auth-perk-item">
                    <span className="auth-perk-icon">✓</span>
                    <span>Verifiable Completion Credentials</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="auth-brand-footer">
              🔒 256-Bit Encrypted Session • Durbhasi Gurukulam Portal
            </div>
          </div>

          {/* Right Form Panel */}
          <div className="auth-form-panel">
            <span className="auth-form-kicker">Student Portal Access</span>
            <h2 className="auth-form-title">Sign In to Your Account</h2>
            <p className="auth-form-subtext">
              Don't have a student account yet?{' '}
              <Link to={redirectQuery ? `/register?redirect=${encodeURIComponent(redirectQuery)}` : '/register'} state={location.state}>
                Register for free &rarr;
              </Link>
            </p>

            {redirectQuery && (
              <div
                style={{
                  background: 'rgba(59, 130, 246, 0.08)',
                  border: '1px solid rgba(59, 130, 246, 0.25)',
                  borderRadius: '8px',
                  padding: '10px 14px',
                  marginBottom: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  color: '#1d4ed8',
                  fontSize: '0.86rem'
                }}
              >
                <span>ℹ️</span>
                <span><strong>Student Sign-In Required:</strong> Please log in to complete course enrollment.</span>
              </div>
            )}

            {error && (
              <div className="auth-error-alert">
                <span>⚠️</span>
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="auth-form">
              <div className="auth-input-group">
                <label className="auth-input-label">
                  Email Address <span>*</span>
                </label>
                <div className="auth-input-box">
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="student@example.com"
                    className="auth-input-field"
                    value={formData.email}
                    onChange={handleChange}
                    disabled={loading}
                    autoComplete="email"
                  />
                </div>
              </div>

              <div className="auth-input-group">
                <label className="auth-input-label">
                  Password <span>*</span>
                </label>
                <div className="auth-input-box">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    required
                    placeholder="Enter your password"
                    className="auth-input-field"
                    value={formData.password}
                    onChange={handleChange}
                    disabled={loading}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    className="auth-pw-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex="-1"
                  >
                    {showPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '6px' }}>
                  <Link
                    to="/forgot-password"
                    style={{
                      color: 'var(--color-primary, #2563eb)',
                      fontSize: '0.84rem',
                      textDecoration: 'none',
                      fontWeight: 500
                    }}
                  >
                    Forgot Password?
                  </Link>
                </div>
              </div>

              <button
                type="submit"
                className="auth-submit-btn"
                disabled={loading}
              >
                {loading ? 'Authenticating...' : 'Sign In to Portal →'}
              </button>
            </form>

            <div className="auth-card-footer">
              <span>Need counseling or quick help? </span>
              <Link to="/support">Contact Support Desk</Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Login;
